<script setup lang="ts">
import { computed } from 'vue'
import { ElTooltip } from 'element-plus'
import { HandPointerIcon, MagnifyingPlusIcon, MagnifyingMinusIcon } from 'Common/components/elements/icons/Icons'
import { AnnotatorModes } from 'Modules/types/pages/imageReview/enums'
import MarkerTypeSelector from './MarkerTypeSelector.vue'
import ToolbarHelp from './ToolbarHelp.vue'

//Types
import { MarkerTypes } from 'Modules/types/pages/imageReview/enums'

interface PropTypes {
  mode: AnnotatorModes
  setMode: (newMode: AnnotatorModes) => void
  setType: (markerTpe: MarkerTypes) => void
  setZoomLevel: (newZoomLevel: number) => void
  zoomLevel: number
  selectedMarkerType: MarkerTypes
}

const props = defineProps<PropTypes>()
const MAX_ZOOM_PERCENTAGE = 100
const ZOOM_INCREMENT = 2.5
const ZOOM_PERCENTAGE_STEPS = Array.from(
  { length: Math.floor(MAX_ZOOM_PERCENTAGE / ZOOM_INCREMENT) + 1 },
  (_, i) => i * ZOOM_INCREMENT
)
const showTooltipAfter = 1000

const handleMarkerTypeUpdate = (markerType: MarkerTypes) => {
  switchToCreateMode()
  props.setType(markerType)
}

const zoomInClass = computed<string>(() => {
  if (zoomInDisabled() === true) return 'text-gray-400'
  return 'text-white cursor-pointer'
})

const zoomOutClass = computed<string>(() => {
  if (zoomOutDisabled() === true) return 'text-gray-400'
  return 'text-white cursor-pointer'
})

const switchToCreateMode = (): void => {
  props.setMode(AnnotatorModes.Create)
}

const switchToSelectMode = (): void => {
  props.setMode(AnnotatorModes.Select)
}

const zoomInDisabled = (): boolean => {
  return props.zoomLevel === ZOOM_PERCENTAGE_STEPS[ZOOM_PERCENTAGE_STEPS.length - 1]
}

const zoomOutDisabled = (): boolean => {
  return props.zoomLevel === ZOOM_PERCENTAGE_STEPS[0]
}

const getNextZoomPercentage = (): number => {
  const currZoomIndex = ZOOM_PERCENTAGE_STEPS.findIndex((percentage) => percentage === props.zoomLevel)
  if (currZoomIndex < ZOOM_PERCENTAGE_STEPS.length - 1) {
    return ZOOM_PERCENTAGE_STEPS[currZoomIndex + 1]
  }
  return props.zoomLevel
}

const getPreviousZoomPercentage = (): number => {
  const currZoomIndex = ZOOM_PERCENTAGE_STEPS.findIndex((percentage) => percentage === props.zoomLevel)
  if (currZoomIndex !== 0) {
    return ZOOM_PERCENTAGE_STEPS[currZoomIndex - 1]
  }
  return props.zoomLevel
}

const zoomIn = (): void => {
  if (zoomInDisabled() === false) {
    const nextZoomPercentage = getNextZoomPercentage()
    props.setZoomLevel(nextZoomPercentage)
  }
}

const zoomOut = (): void => {
  if (zoomOutDisabled() === false) {
    const previousZoomPercentage = getPreviousZoomPercentage()
    props.setZoomLevel(previousZoomPercentage)
  }
}
</script>

<template>
  <div id="annotator-toolbar" class="h-full w-4/5 px-10 z-10 flex items-center justify-start">
    <el-tooltip placement="bottom" content="Marker Selection Mode" :show-after="showTooltipAfter">
      <span
        :class="`text-white h-full w-min px-4 flex items-center justify-center cursor-pointer${
          props.mode === 'select' ? ' bg-gray-600' : ''
        }`"
        @click="switchToSelectMode"
      >
        <HandPointerIcon />
      </span>
    </el-tooltip>
    <MarkerTypeSelector
      :mode="props.mode"
      :selected-marker-type="props.selectedMarkerType"
      :show-tooltip-after="showTooltipAfter"
      @change="handleMarkerTypeUpdate"
    />
    <el-tooltip placement="bottom" content="Zoom In" :show-after="showTooltipAfter">
      <span :class="`text-white h-full w-min px-4 flex items-center justify-center ${zoomInClass}`" @click="zoomIn">
        <MagnifyingPlusIcon />
      </span>
    </el-tooltip>
    <el-tooltip placement="bottom" content="Zoom Out" :show-after="showTooltipAfter">
      <span :class="`text-white h-full w-min px-4 flex items-center justify-center ${zoomOutClass}`" @click="zoomOut">
        <MagnifyingMinusIcon />
      </span>
    </el-tooltip>
    <span :class="`text-white h-full w-28 px-4 flex items-center justify-center`" @click="zoomOut">
      <el-select :model-value="props.zoomLevel" placeholder="Zoom Level" size="small" @change="props.setZoomLevel">
        <el-option v-for="step in ZOOM_PERCENTAGE_STEPS" :key="step" :label="`${step}%`" :value="step" />
      </el-select>
    </span>
    <span>
      <ToolbarHelp />
    </span>
  </div>
</template>
